/* eslint-disable no-console */
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { Link } from 'react-router-dom';
import i18n from 'i18next'; // Import i18next

import { useStoreDetails } from '../../context/Store/StoreProvider';

import {
  makeStyles,
  RACCheckBox,
  RACCOLOR,
  RACTable,
  RACTableCell,
  RACTableRow,
  Typography,
} from '@rentacenter/racstrap';
import { useTranslation } from 'react-i18next';
import {
  PriorityFieldCustomerStateContext,
  usePriorityFieldCustomerActions,
} from '../../context/PriorityFieldVisit/PriorityFieldProvider';
import clsx from 'clsx';
import { PriorityCustomerDTO } from '../../domain/priorityFieldVisit/PriorityCustomerDTO';
import InfiniteScroll from 'react-infinite-scroll-component';
import {
  ApiStateWrapper,
  Loader,
} from '../../common/ApiStateWrapper/ApiStateWrapper';
import { AppRoute } from '../../config/route-config';
import { ScheduleFieldVisitDTO } from '../../types/types';
import { ReactComponent as DownArrow } from '../../assets/img/down-arrow.svg';
import { ReactComponent as UpArrow } from '../../assets/img/up-arrow.svg';

export const FULL_API_ERROR_MESSAGE =
  'Unable to fetch the data. Please try again later.';
export const INITIAL_OFFSET = 0;
export const LIST_API_LIMIT = 500;

const useClasses = makeStyles((theme: any) => ({
  customerListWrapper: {
    overflowX: 'auto',
    height: 'calc(100vh - 200px)',
    fontSize: theme.typography.pxToRem(12),
    '& td, & th': {
      fontSize: theme.typography.pxToRem(12),
      padding: theme.typography.pxToRem(6),
    },
  },
  header: {
    fontSize: '1rem',
    marginBottom: '.5rem',
  },
  cardBody: {
    padding: '.5rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  centerall: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: theme.typography.pxToRem(170),
    textAlign: 'center',
  },
  checkbox: {
    padding: '0',
  },
  noLeftPadding: {
    paddingLeft: 0,
  },
  selected: {
    backgroundColor: 'none !important',
  },
  statusPadding: {
    width: '15%',
  },
  leftPadding21: {
    paddingLeft: theme.typography.pxToRem(21),
  },
  paddingTopBottom8: {
    paddingTop: '.25rem',
    paddingBottom: '.25rem',
  },
  rightField: {
    marginRight: theme.typography.pxToRem(5),
  },
  hideIt: {
    display: 'none',
  },
  link: {
    color: RACCOLOR.BLUE_CRAYOLA,
    cursor: 'pointer',
  },
  contentHeight: {
    height: 'calc(100vh - 200px)',
  },
  tipRoot: {
    borderLeftWidth: theme.typography.pxToRem(6),
  },
  sortableHeader: {
    cursor: 'pointer',
    userSelect: 'none' as const,
  },
}));
const language = window.sessionStorage.getItem('language') || 'en';
export const fieldVisitPriorityWrapperTestId =
  'fieldVisitPriorityWrapperTestId';

const tableHeaderList = [
  'Rank',
  '[]',
  'Customer',
  'DPD',
  'DE Level',
  'DPD Days',
  'Agreement',
  'Action',
  'Special Offer',
  'Product Type',
  'SubType',
  'Extension Offer',
  'EPO Offer',
  'Rate Offer',
  'Term Offer',
  'Status',
  'Phone Number',
  'AddressLine1',
  'AddressLine2',
  'City',
  'State',
  'Zip',
  'FPD7',
  'Remaining Value',
  'Customer Type',
  'Extension Offer Description',
  'EPO Offer Description',
  'Rate Offer Description',
  'Term Offer Description',
  'On Prior Week',
];

const sortByDpd = (
  list: PriorityCustomerDTO[],
  order: 'asc' | 'desc'
): PriorityCustomerDTO[] =>
  [...list].sort((a, b) => {
    const aNum = parseInt(a.daysLate, 10) || 0;
    const bNum = parseInt(b.daysLate, 10) || 0;
    return order === 'desc' ? bNum - aNum : aNum - bNum;
  });

/**
 * DE Level sort priority (higher number = higher tier).
 * Descending order: Level 5 → Level 0 → Declined → Missing (–)
 * Ascending order:  Missing (–) → Declined → Level 0 → Level 5
 */
const DE_LEVEL_PRIORITY: Record<string, number> = {
  '5': 5,
  '4': 4,
  '3': 3,
  '2': 2,
  '1': 1,
  '0': 0,
  '-1': -1,
  Declined: -1,
};
const DE_LEVEL_MISSING_PRIORITY = -2; // null / '–' / '-' / empty

const getDeLevelPriority = (deLevel: string | null | undefined): number => {
  if (!deLevel) return DE_LEVEL_MISSING_PRIORITY;
  return DE_LEVEL_PRIORITY[deLevel] ?? DE_LEVEL_MISSING_PRIORITY;
};

const getDeLevelDisplay = (deLevel: string | null | undefined): string => {
  if (!deLevel || deLevel === '-' || deLevel === '–') {
    return '–';
  }

  if (deLevel === '-1' || deLevel === 'Declined') {
    return 'Declined';
  }

  return `Level ${deLevel}`;
};

const sortByDeLevel = (
  list: PriorityCustomerDTO[],
  order: 'asc' | 'desc'
): PriorityCustomerDTO[] =>
  [...list].sort((a, b) => {
    const aVal = getDeLevelPriority(a.deLevel);
    const bVal = getDeLevelPriority(b.deLevel);
    return order === 'desc' ? bVal - aVal : aVal - bVal;
  });

// eslint-disable-next-line sonarjs/cognitive-complexity
export const PriorityCustomerList = () => {
  const { t } = useTranslation();
  const classes = useClasses();
  const colorIdx = useRef(0);
  const [offset, setOffset] = useState<number>(INITIAL_OFFSET);
  const [selected, setSelected] = useState<string[]>([]);
  const [selectAll, setSelectAll] = useState<boolean>(false);
  const [dpdSortOrder, setDpdSortOrder] = useState<'asc' | 'desc' | null>(null);
  const [deLevelSortOrder, setDeLevelSortOrder] = useState<
    'asc' | 'desc' | null
  >(null);
  const [activeSort, setActiveSort] = useState<'dpd' | 'deLevel' | null>(null);

  const { enableFieldVisitStrategy, PRIORITY_FIELD_VISIT_CONFIG } =
    useStoreDetails();

  const {
    loading,
    hasApiError,
    selectedCustomerIds,
    scheduleFieldVisitDto,
    exportToXls,
    priorityFieldVisitCustomerDetails,
  } = useContext(PriorityFieldCustomerStateContext);
  const {
    onCustomerSelectChanged,
    setSelectedCustomerIds,
    buildFieldVisitXferList,
  } = usePriorityFieldCustomerActions();

  useEffect(() => {
    if (!selectedCustomerIds?.length) {
      setSelected(selectedCustomerIds);
    }
  }, [selectedCustomerIds]);

  useEffect(() => {
    onCustomerSelectChanged(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleSelectAll = () => {
    setSelectAll(!selectAll);
    const newSelected: string[] = [];
    const oneJump: ScheduleFieldVisitDTO[] = [];
    if (!selectAll) {
      priorityFieldVisitCustomerDetails?.map(
        (oneCustomer: PriorityCustomerDTO) => {
          newSelected.push(oneCustomer.customer.customerId);
          oneJump.push({
            name:
              oneCustomer.customer.firstName +
              ' ' +
              oneCustomer.customer.lastName,
            customerId: oneCustomer.customer.customerId,
            agreementNumber: oneCustomer.agreementNumber,
          });
        }
      );
    }
    buildFieldVisitXferList(oneJump);
    setSelected(newSelected);

    onCustomerSelectChanged(true);
    setSelectedCustomerIds(newSelected);
  };

  const findCustomerInList = (
    customerId: string
  ): PriorityCustomerDTO | undefined => {
    return priorityFieldVisitCustomerDetails.find(
      (one) => one.customer.customerId == customerId
    );
  };
  const isSelected = (selectedItems: string[], item: string) =>
    selectedItems.includes(item);

  const handleDpdSort = () => {
    setDpdSortOrder((prev) =>
      activeSort !== 'dpd' ? 'desc' : prev === 'desc' ? 'asc' : 'desc'
    );
    setActiveSort('dpd');
  };

  const handleDeLevelSort = () => {
    setDeLevelSortOrder((prev) =>
      activeSort !== 'deLevel' ? 'desc' : prev === 'desc' ? 'asc' : 'desc'
    );
    setActiveSort('deLevel');
  };

  const sortedCustomers = useMemo((): PriorityCustomerDTO[] => {
    if (!priorityFieldVisitCustomerDetails?.length) {
      return priorityFieldVisitCustomerDetails;
    }
    if (activeSort === 'dpd' && dpdSortOrder) {
      return sortByDpd(priorityFieldVisitCustomerDetails, dpdSortOrder);
    }
    if (activeSort === 'deLevel' && deLevelSortOrder) {
      return sortByDeLevel(priorityFieldVisitCustomerDetails, deLevelSortOrder);
    }
    return priorityFieldVisitCustomerDetails;
  }, [
    activeSort,
    dpdSortOrder,
    deLevelSortOrder,
    priorityFieldVisitCustomerDetails,
  ]);

  const handleRowSelectionChange = (item: string) => {
    let newSelected: string[] = [];
    let oneJump: ScheduleFieldVisitDTO[] = scheduleFieldVisitDto;

    const oneCustomer = findCustomerInList(item);

    if (selected.includes(item)) {
      newSelected = selected.filter((selectedItem) => selectedItem !== item);
      oneJump = oneJump.filter(
        (selectedItem) => selectedItem.customerId !== item
      );
    } else {
      newSelected = newSelected.concat(selected, item);
      if (oneCustomer) {
        oneJump.push({
          name:
            oneCustomer.customer.firstName +
            ' ' +
            oneCustomer.customer.lastName,
          customerId: oneCustomer.customer.customerId,
          agreementNumber: oneCustomer.agreementNumber,
        });
      }
    }

    buildFieldVisitXferList(oneJump);
    setSelected(newSelected);

    if (newSelected.length > 0) {
      onCustomerSelectChanged(true);
    } else {
      onCustomerSelectChanged(false);
    }
    setSelectedCustomerIds(newSelected);
  };

  const renderSortableHeader = (
    col: string,
    index: number,
    onClick: () => void,
    sortOrder: 'asc' | 'desc' | null
  ) => (
    <RACTableCell
      key={index}
      classes={{
        root: clsx(classes.paddingTopBottom8, classes.sortableHeader),
      }}
    >
      <span
        onClick={onClick}
        style={{
          cursor: 'pointer',
          userSelect: 'none',
          whiteSpace: 'nowrap',
          display: 'inline-flex',
          alignItems: 'center',
          gap: '4px',
        }}
      >
        {t(col)}
        {sortOrder === null && (
          <span style={{ display: 'inline-flex', gap: 2 }}>
            <DownArrow style={{ width: '8px', height: '8px', opacity: 0.6 }} />
            <UpArrow style={{ width: '8px', height: '8px', opacity: 0.6 }} />
          </span>
        )}
        {sortOrder === 'desc' && (
          <DownArrow style={{ width: '10px', height: '10px' }} />
        )}
        {sortOrder === 'asc' && (
          <UpArrow style={{ width: '10px', height: '10px' }} />
        )}
      </span>
    </RACTableCell>
  );

  const renderHeaderCell = (col: string, index: number) => {
    if (index === 15) {
      return (
        <RACTableCell
          key={index}
          padding="checkbox"
          classes={{ root: clsx(classes.statusPadding) }}
        >
          {t(col)}
        </RACTableCell>
      );
    }
    if (index === 0) {
      return (
        <RACTableCell
          key={index}
          padding="checkbox"
          classes={{
            root: clsx(classes.leftPadding21, classes.paddingTopBottom8),
          }}
        >
          {t(col)}
        </RACTableCell>
      );
    }
    if (enableFieldVisitStrategy && index === 1) {
      return (
        <RACTableCell
          key={index}
          padding="checkbox"
          classes={{
            root: clsx(classes.leftPadding21, classes.paddingTopBottom8),
          }}
        >
          <RACCheckBox
            id="999"
            color="primary"
            onChange={() => toggleSelectAll()}
            checked={selectAll}
            classes={{ checkbox: classes.checkbox }}
          />
        </RACTableCell>
      );
    }
    if (index > 15) {
      return (
        <RACTableCell
          key={index}
          padding="checkbox"
          classes={{ root: clsx(classes.hideIt) }}
        >
          {t(col)}
        </RACTableCell>
      );
    }
    // DE Level column - only visible and sortable when flag is ON
    if (index === 4) {
      if (!PRIORITY_FIELD_VISIT_CONFIG) {
        return (
          <RACTableCell key={index} classes={{ root: classes.hideIt }}>
            {t(col)}
          </RACTableCell>
        );
      }
      return renderSortableHeader(
        col,
        index,
        handleDeLevelSort,
        deLevelSortOrder
      );
    }
    // DPD Days column - always visible, sortable only when flag is ON
    if (index === 5) {
      if (!PRIORITY_FIELD_VISIT_CONFIG) {
        return (
          <RACTableCell
            key={index}
            classes={{ root: classes.paddingTopBottom8 }}
          >
            {t(col)}
          </RACTableCell>
        );
      }
      return renderSortableHeader(col, index, handleDpdSort, dpdSortOrder);
    }
    return (
      <RACTableCell key={index} classes={{ root: classes.paddingTopBottom8 }}>
        {t(col)}
      </RACTableCell>
    );
  };

  const renderTableHead = () => (
    <>{tableHeaderList.map((col, index) => renderHeaderCell(col, index))}</>
  );

  const FVC = ' Field Vist Complete ';
  const COM = ' Commitment Open ';
  const genStat = (stat: any) => {
    if (stat == 'CMP') return FVC;
    if (stat == 'DEL') return FVC;
    if (stat == 'CAN') return FVC;
    if (stat == 'TD') return ' Field Vist Open ';
    if (stat == 'UP') return COM;
    if (stat == 'OP') return COM;
    if (stat == 'GD') return COM;
    if (stat == 'BR') return ' Commitment Complete ';
    if (stat == 'BKPD') return ' Commitment Complete ';
    return '';
  };

  const fetchNextPage = () => {
    /*
    fetchNextPage is used by infinite scroll component
    the first fetch is handled by our component, therefore we are not letting the
    infinite scroll to trigger the first request.
    Handles the case when you apply the filters
    (offset set to 0 -> request is triggered, but infinite scroll is also triggering a request, because its data length is 0)
    */

    setOffset(offset + LIST_API_LIMIT);
  };

  const renderCustomerRow = (
    oneCustomer: PriorityCustomerDTO,
    index: number
  ) => (
    <RACTableRow
      key={oneCustomer.priorityId}
      hover
      backgroundColor={
        colorIdx.current++ % 2 == 0 ? RACCOLOR?.WHITE : RACCOLOR?.ALICE_BLUE
      }
    >
      <RACTableCell>{index + 1}</RACTableCell>
      <RACTableCell>
        <RACCheckBox
          id={oneCustomer.customer.customerId}
          color="primary"
          onChange={() =>
            handleRowSelectionChange(oneCustomer.customer.customerId)
          }
          checked={isSelected(
            selectedCustomerIds,
            oneCustomer.customer.customerId
          )}
          classes={{ checkbox: classes.checkbox }}
        />
      </RACTableCell>
      <RACTableCell>
        <Link
          style={{ color: 'inherit' }}
          to={{
            pathname: `${AppRoute.Customer}/${oneCustomer.customer.customerId}`,
          }}
        >
          {oneCustomer.customer.firstName} {oneCustomer.customer.lastName}
        </Link>
      </RACTableCell>
      <RACTableCell>{oneCustomer.lateDescription}</RACTableCell>
      <RACTableCell
        className={PRIORITY_FIELD_VISIT_CONFIG ? undefined : classes.hideIt}
      >
        {getDeLevelDisplay(oneCustomer.deLevel)}
      </RACTableCell>
      <RACTableCell>{oneCustomer.daysLate}</RACTableCell>
      <RACTableCell>{oneCustomer.agreementNumber}</RACTableCell>
      <RACTableCell>{i18n.t(oneCustomer.fieldAction)}</RACTableCell>
      <RACTableCell>{i18n.t(oneCustomer.specialOffer)}</RACTableCell>
      <RACTableCell>
        {language === 'es-MX' || language === 'es-US'
          ? oneCustomer.productTypeEs
          : oneCustomer.productType}
      </RACTableCell>
      <RACTableCell>
        {language === 'es-MX' || language === 'es-US'
          ? oneCustomer.subTypeEs
          : oneCustomer.subType}
      </RACTableCell>
      <RACTableCell>{i18n.t(oneCustomer.extensionOffer)}</RACTableCell>
      <RACTableCell>{oneCustomer.epoOfferFlag}</RACTableCell>
      <RACTableCell>{i18n.t(oneCustomer.rateOffer)}</RACTableCell>
      <RACTableCell>{i18n.t(oneCustomer.termOffer)}</RACTableCell>
      <RACTableCell>
        {genStat(oneCustomer.fieldvisit)}{' '}
        {genStat(oneCustomer.customer.commitment)}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.phoneNumber}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.addressLine1}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.addressLine2}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.city}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.stateName}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.customer.zip}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.fpd7 == '1' ? 'Y' : 'N'}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.agreementRV}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.description}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.extensionOffer}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.epoOffer}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.rateOffer}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.termOffer}
      </RACTableCell>
      <RACTableCell className={classes.hideIt}>
        {oneCustomer.priorWeekDelinquent == '1' ? 'Y' : 'N'}
      </RACTableCell>
    </RACTableRow>
  );

  const renderTableContent = () => {
    colorIdx.current = 0;
    return (
      <>
        {sortedCustomers?.length > 0 && sortedCustomers?.map(renderCustomerRow)}
        {sortedCustomers?.length == 0 && (
          <Typography className={classes.centerall}>
            {t('No Priority results returned for this reporting period')}
          </Typography>
        )}
      </>
    );
  };

  return (
    <div
      className={clsx(classes.customerListWrapper)}
      data-testid={fieldVisitPriorityWrapperTestId}
    >
      <InfiniteScroll
        next={fetchNextPage}
        dataLength={5}
        hasMore={false}
        height="calc(100vh - 200px)"
        loader={loading ? <Loader /> : null}
      >
        <ApiStateWrapper
          // show loading only for the first request
          loading={loading}
          // show error only if the first request fails
          hasApiError={hasApiError}
          response={priorityFieldVisitCustomerDetails}
          noItemAdditionalText={t(
            '!   No Priority results returned for this reporting period'
          )}
          successContent={
            <div ref={exportToXls}>
              <RACTable
                key={`${activeSort}-${dpdSortOrder}-${deLevelSortOrder}`}
                renderTableHead={renderTableHead}
                renderTableContent={renderTableContent}
                stickyHeader
              />
            </div>
          }
          classes={{
            loader: classes.contentHeight,
            apiError: classes.contentHeight,
            noItems: classes.contentHeight,
          }}
        />
      </InfiniteScroll>
    </div>
  );
};
